import React from "react";
import { errorMessage, isPlaceholderEmail } from "@atlas/shared";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Search, Users, X, ExternalLink, ShieldAlert, ChevronRight } from "lucide-react";
import {
  fetchAdminUsers,
  fetchAdminUser,
  fetchAdminUserUsage,
  patchAdminUser,
  revokeAdminUserSessions,
  deleteAdminUser,
  grantAdminUserWaypoints,
  type AdminUserSummary,
  type AdminUserPatch,
  type AdminUserListParams,
} from "../../api/admin";
import { Badge } from "../../components/Badge";
import { Button } from "../../components/Button";
import { Skel } from "../../components/Skeleton";
import { EmptyState } from "../../components/EmptyState";
import { useAuth } from "../../hooks/useAuth";
import { AdminShell, AccessDenied } from "./AdminTabs";
import { OpenShowcaseButton } from "./AdminUserActivitiesPage";
import { UserEmailsPanel } from "./UserEmailsPanel";
import { SubscriptionPanel } from "./SubscriptionPanel";
import { ClientGlyph, Segmented, formatRelative, formatDateTime, screenLabel } from "./usage-ui";

const PAGE_SIZE = 50;

const inputStyle: React.CSSProperties = {
  width: "100%",
  height: 34,
  padding: "0 10px",
  background: "var(--surface-sunken)",
  border: "1px solid var(--border-default)",
  borderRadius: "var(--radius-md)",
  color: "var(--text-primary)",
  fontSize: 13,
  outline: "none",
  boxSizing: "border-box",
};

function useDebounced<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

function formatDate(value: string | null | undefined) {
  return value ? new Date(value).toLocaleDateString() : "—";
}

function PlanBadge({ plan }: { plan: string }) {
  return <Badge variant={plan === "pro" ? "gold" : "neutral"}>{plan}</Badge>;
}

// ─── Delete user dialog (type-DELETE confirm, mirrors ProfilePage pattern) ────

function DeleteUserDialog({
  email,
  onCancel,
  onConfirm,
  pending,
  error,
}: {
  email: string;
  onCancel: () => void;
  onConfirm: () => void;
  pending: boolean;
  error: string | null;
}) {
  const [text, setText] = React.useState("");

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.6)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1100,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div
        style={{
          background: "var(--surface-card)",
          border: "1px solid var(--border-strong)",
          borderRadius: "var(--radius-xl)",
          padding: 28,
          maxWidth: 420,
          width: "90%",
          boxShadow: "var(--shadow-xl)",
        }}
      >
        <h2 style={{ font: "var(--type-h3)", color: "var(--text-primary)", marginBottom: 8 }}>
          Delete this account?
        </h2>
        <p style={{ fontSize: 14, color: "var(--text-secondary)", marginBottom: 16, lineHeight: 1.5 }}>
          This anonymises <strong style={{ color: "var(--text-primary)" }}>{email}</strong>, removes
          their sessions and connected identities, and cannot be undone. Type{" "}
          <span style={{ fontFamily: "var(--font-mono)" }}>DELETE</span> to confirm.
        </p>
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="DELETE"
          autoFocus
          style={{ ...inputStyle, fontFamily: "var(--font-mono)", marginBottom: 16 }}
        />
        {error && (
          <p style={{ fontSize: 13, color: "var(--status-danger)", marginBottom: 12 }}>{error}</p>
        )}
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <Button variant="ghost" onClick={onCancel}>Cancel</Button>
          <Button
            variant="danger"
            disabled={text !== "DELETE" || pending}
            onClick={onConfirm}
          >
            {pending ? "Deleting…" : "Delete account"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Grant Waypoints control (STO-1 admin grant) ──────────────────────────────

function GrantWaypointsControl({ userId }: { userId: string }) {
  const queryClient = useQueryClient();
  const [delta, setDelta] = React.useState("");
  const [note, setNote] = React.useState("");

  const grantMutation = useMutation({
    mutationFn: () =>
      grantAdminUserWaypoints(userId, {
        delta: parseInt(delta, 10),
        note: note.trim() || undefined,
      }),
    onSuccess: () => {
      setDelta("");
      setNote("");
      void queryClient.invalidateQueries({ queryKey: ["admin/audit-log"] });
    },
  });

  const parsed = parseInt(delta, 10);
  const valid = Number.isInteger(parsed) && parsed !== 0;

  return (
    <div>
      <p className="eyebrow" style={{ fontSize: 10, marginBottom: 6 }}>Grant Waypoints</p>
      <div style={{ display: "grid", gridTemplateColumns: "90px 1fr auto", gap: 8, alignItems: "center" }}>
        <input
          type="number"
          placeholder="+500"
          value={delta}
          onChange={(e) => setDelta(e.target.value)}
          style={{ ...inputStyle, height: 30 }}
        />
        <input
          type="text"
          placeholder="Note (audit log)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          style={{ ...inputStyle, height: 30 }}
        />
        <Button
          variant="secondary"
          size="sm"
          disabled={!valid || grantMutation.isPending}
          onClick={() => grantMutation.mutate()}
        >
          {grantMutation.isPending ? "Granting…" : "Grant"}
        </Button>
      </div>
      {grantMutation.isSuccess && (
        <p className="tnum" style={{ fontSize: 12, color: "var(--spruce-300)", margin: "6px 0 0", fontFamily: "var(--font-mono)" }}>
          New balance: {grantMutation.data.waypoints.toLocaleString()} Waypoints
        </p>
      )}
      {grantMutation.isError && (
        <p style={{ fontSize: 12, color: "var(--status-danger)", margin: "6px 0 0" }}>
          {errorMessage(grantMutation.error, "Grant failed")}
        </p>
      )}
    </div>
  );
}

// ─── User detail drawer ───────────────────────────────────────────────────────

function UserUsagePanel({ userId }: { userId: string }) {
  const [days, setDays] = React.useState<7 | 30 | 90>(30);
  const usageQuery = useQuery({
    queryKey: ["admin/usage/users", userId, days],
    queryFn: () => fetchAdminUserUsage(userId, days),
  });
  const usage = usageQuery.data?.usage;
  const totalByClient = usage ? usage.byClient.web + usage.byClient.ios + usage.byClient.android : 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <span className="eyebrow" style={{ fontSize: 10 }}>Usage</span>
        <Segmented
          value={String(days) as "7" | "30" | "90"}
          options={[{ value: "7", label: "7d" }, { value: "30", label: "30d" }, { value: "90", label: "90d" }]}
          onChange={(v) => setDays(Number(v) as 7 | 30 | 90)}
        />
      </div>

      {usageQuery.isLoading && <Skel height={80} />}

      {usage && usage.views === 0 && (
        <p style={{ fontSize: 12, color: "var(--text-muted)", margin: 0 }}>
          No screen views recorded in the last {days} days.
        </p>
      )}

      {usage && usage.views > 0 && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
            {[
              { label: "Visits", value: usage.visits },
              { label: "Screen views", value: usage.views },
              { label: "Per visit", value: usage.visits > 0 ? Math.round((usage.views / usage.visits) * 10) / 10 : 0 },
            ].map((stat) => (
              <div key={stat.label} style={{ background: "var(--surface-sunken)", borderRadius: "var(--radius-md)", padding: "8px 10px" }}>
                <div className="eyebrow" style={{ fontSize: 9 }}>{stat.label}</div>
                <div className="tnum" style={{ fontFamily: "var(--font-heading)", fontSize: 20, color: "var(--text-primary)" }}>
                  {stat.value.toLocaleString()}
                </div>
              </div>
            ))}
          </div>

          {totalByClient > 0 && (
            <div style={{ display: "flex", gap: 12, fontSize: 12, color: "var(--text-secondary)", flexWrap: "wrap" }}>
              {(["web", "ios", "android"] as const)
                .filter((k) => usage.byClient[k] > 0)
                .map((k) => (
                  <span key={k} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                    <ClientGlyph client={k} withLabel />
                    <span className="tnum">{Math.round((usage.byClient[k] / totalByClient) * 100)}%</span>
                  </span>
                ))}
            </div>
          )}

          <div>
            <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 4 }}>Top screens</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {usage.topScreens.slice(0, 6).map((screen) => {
                const pct = Math.round((screen.views / usage.views) * 100);
                return (
                  <div key={screen.name} style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 40px", gap: 8, alignItems: "center", fontSize: 12 }}>
                    <div style={{ position: "relative", height: 18, borderRadius: 3, background: "var(--surface-sunken)", overflow: "hidden" }}>
                      <div style={{ position: "absolute", inset: 0, width: `${pct}%`, background: "var(--accent-soft)" }} />
                      <span style={{ position: "relative", padding: "0 6px", lineHeight: "18px", color: "var(--text-primary)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "block" }}>
                        {screenLabel(screen.name)}
                      </span>
                    </div>
                    <span className="tnum" style={{ textAlign: "right", color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>{screen.views}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div>
            <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 4 }}>Recent visits</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {usage.recentVisits.slice(0, 6).map((visit) => (
                <div key={visit.startedAt} style={{ fontSize: 12, lineHeight: 1.45 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8, color: "var(--text-secondary)" }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                      <ClientGlyph client={visit.client} />
                      <span className="tnum">{formatDateTime(visit.startedAt)}</span>
                    </span>
                    <span className="tnum" style={{ color: "var(--text-muted)" }}>{visit.views} view{visit.views === 1 ? "" : "s"}</span>
                  </div>
                  <div style={{ color: "var(--text-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={visit.screens.map(screenLabel).join(" › ")}>
                    {visit.screens.slice(0, 6).map(screenLabel).join(" › ")}{visit.screens.length > 6 ? " › …" : ""}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {usage && usage.sessions.length > 0 && (
        <div>
          <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 4 }}>Live sessions</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 3, fontSize: 12 }}>
            {usage.sessions.slice(0, 6).map((session) => (
              <div key={session.id} style={{ display: "flex", justifyContent: "space-between", gap: 8, color: "var(--text-secondary)" }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <ClientGlyph client={session.client} withLabel />
                  {session.appVersion && (
                    <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text-muted)" }}>{session.appVersion}</span>
                  )}
                </span>
                <span className="tnum" style={{ color: "var(--text-muted)" }} title={session.lastSeenAt ?? undefined}>
                  {session.lastSeenAt ? `seen ${formatRelative(session.lastSeenAt)}` : "not seen since deploy"}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function UserDrawer({ userId, onClose }: { userId: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user: me } = useAuth();
  const [showDelete, setShowDelete] = React.useState(false);
  const [confirmAction, setConfirmAction] = React.useState<null | "revoke">(null);

  const detailQuery = useQuery({
    queryKey: ["admin/users", userId],
    queryFn: () => fetchAdminUser(userId),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["admin/users"] });
    void queryClient.invalidateQueries({ queryKey: ["admin/audit-log"] });
  };

  const patchMutation = useMutation({
    mutationFn: (patch: AdminUserPatch) => patchAdminUser(userId, patch),
    onSuccess: invalidate,
  });

  const revokeMutation = useMutation({
    mutationFn: () => revokeAdminUserSessions(userId),
    onSuccess: () => {
      setConfirmAction(null);
      invalidate();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteAdminUser(userId),
    onSuccess: () => {
      invalidate();
      onClose();
    },
  });

  const detail = detailQuery.data;
  const user = detail?.user;
  const isSelf = me?.id === userId;

  const rowStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    padding: "10px 0",
    borderBottom: "1px solid var(--border-subtle)",
  };
  const labelStyle: React.CSSProperties = {
    fontSize: 13,
    fontWeight: 600,
    color: "var(--text-secondary)",
  };

  return (
    <div
      style={{ position: "fixed", inset: 0, zIndex: 1000, display: "flex", justifyContent: "flex-end" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div aria-hidden="true" style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,.45)" }} onClick={onClose} />
      <div
        style={{
          position: "relative",
          width: 440,
          maxWidth: "92vw",
          height: "100%",
          background: "var(--surface-card)",
          borderLeft: "1px solid var(--border-strong)",
          boxShadow: "var(--shadow-xl)",
          display: "flex",
          flexDirection: "column",
          overflow: "auto",
        }}
      >
        {/* Header */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", padding: "20px 22px 14px", borderBottom: "1px solid var(--border-subtle)" }}>
          <div style={{ minWidth: 0 }}>
            <p className="eyebrow" style={{ fontSize: 10, marginBottom: 4 }}>Explorer</p>
            <div style={{ fontSize: 16, fontWeight: 600, color: "var(--text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {user?.name ?? "—"}
            </div>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--text-muted)", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {user ? displayEmail(user.email) : ""}
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", padding: 4 }}
          >
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: "16px 22px 28px", display: "flex", flexDirection: "column", gap: 22 }}>
          {detailQuery.isLoading && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <Skel height={20} />
              <Skel height={20} />
              <Skel height={20} />
              <Skel height={120} />
            </div>
          )}

          {user && (
            <>
              <Button
                variant="secondary"
                size="sm"
                block
                rightIcon={<ChevronRight size={14} />}
                onClick={() => void navigate({ to: "/admin/users/$userId", params: { userId } })}
              >
                Full profile: collections, achievements, health
              </Button>

              {/* ── Summary ──────────────────────────────────────────── */}
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
                <PlanBadge plan={user.plan} />
                {user.isAdmin && <Badge variant="danger" icon={<ShieldAlert size={11} />}>Admin</Badge>}
                {user.emailVerified ? <Badge variant="success">Verified</Badge> : <Badge variant="neutral">Unverified</Badge>}
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 16px", fontSize: 13 }}>
                <div style={{ color: "var(--text-muted)" }}>Joined</div>
                <div style={{ color: "var(--text-primary)", textAlign: "right" }} className="tnum">{formatDate(user.createdAt)}</div>
                <div style={{ color: "var(--text-muted)" }}>Last seen</div>
                <div style={{ color: "var(--text-primary)", textAlign: "right" }} className="tnum" title={user.lastSeenAt ?? undefined}>{formatRelative(user.lastSeenAt)}</div>
                <div style={{ color: "var(--text-muted)" }}>Last sync</div>
                <div style={{ color: "var(--text-primary)", textAlign: "right" }} className="tnum" title={user.lastSyncAt ?? undefined}>{formatRelative(user.lastSyncAt)}</div>
                <div style={{ color: "var(--text-muted)" }}>Activities</div>
                <div style={{ color: "var(--text-primary)", textAlign: "right" }} className="tnum">{user.activityCount.toLocaleString()}</div>
                <div style={{ color: "var(--text-muted)" }}>Outdoor score</div>
                <div style={{ color: "var(--text-primary)", textAlign: "right" }} className="tnum">{user.outdoorScore.toLocaleString()}</div>
              </div>

              {/* ── Usage (spec 14) ──────────────────────────────────── */}
              <UserUsagePanel userId={userId} />

              {/* ── Emails (spec 16) ─────────────────────────────────── */}
              <UserEmailsPanel userId={userId} />

              {/* ── Subscription (spec 24) ───────────────────────────── */}
              <SubscriptionPanel userId={userId} email={displayEmail(user.email)} />

              {/* ── Account controls ─────────────────────────────────── */}
              <div>
                <p className="eyebrow" style={{ fontSize: 10, marginBottom: 6 }}>Account</p>
                <div style={rowStyle}>
                  <span style={labelStyle}>Admin access</span>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: isSelf ? "not-allowed" : "pointer" }}>
                    <input
                      type="checkbox"
                      checked={user.isAdmin}
                      disabled={patchMutation.isPending || isSelf}
                      onChange={(e) => {
                        const next = e.target.checked;
                        if (window.confirm(next ? `Grant admin access to ${displayEmail(user.email)}?` : `Remove admin access from ${displayEmail(user.email)}?`)) {
                          patchMutation.mutate({ isAdmin: next });
                        }
                      }}
                      style={{ width: 16, height: 16, accentColor: "var(--accent)" }}
                    />
                    <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
                      {isSelf ? "cannot change your own" : user.isAdmin ? "enabled" : "disabled"}
                    </span>
                  </label>
                </div>
                <div style={rowStyle}>
                  <span style={labelStyle}>Email verified</span>
                  {user.emailVerified ? (
                    <span style={{ fontSize: 12, color: "var(--spruce-300)" }}>Verified</span>
                  ) : (
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={patchMutation.isPending}
                      onClick={() => patchMutation.mutate({ emailVerified: true })}
                    >
                      Mark verified
                    </Button>
                  )}
                </div>
                <div style={rowStyle}>
                  <span style={labelStyle}>Sessions</span>
                  {confirmAction === "revoke" ? (
                    <div style={{ display: "flex", gap: 6 }}>
                      <Button variant="ghost" size="sm" onClick={() => setConfirmAction(null)}>Cancel</Button>
                      <Button
                        variant="danger"
                        size="sm"
                        disabled={revokeMutation.isPending}
                        onClick={() => revokeMutation.mutate()}
                      >
                        {revokeMutation.isPending ? "Revoking…" : "Confirm revoke"}
                      </Button>
                    </div>
                  ) : (
                    <Button variant="secondary" size="sm" onClick={() => setConfirmAction("revoke")}>
                      Revoke all sessions
                    </Button>
                  )}
                </div>
                {revokeMutation.isSuccess && (
                  <p style={{ fontSize: 12, color: "var(--spruce-300)", margin: "6px 0 0" }}>
                    {revokeMutation.data.revoked} session{revokeMutation.data.revoked === 1 ? "" : "s"} revoked
                  </p>
                )}
              </div>

              {/* ── Grant Waypoints ──────────────────────────────────── */}
              <GrantWaypointsControl userId={userId} />

              {patchMutation.isError && (
                <p style={{ fontSize: 13, color: "var(--status-danger)", margin: 0 }}>
                  {errorMessage(patchMutation.error, "Update failed")}
                </p>
              )}

              {/* ── Identities ───────────────────────────────────────── */}
              <div>
                <p className="eyebrow" style={{ fontSize: 10, marginBottom: 6 }}>Connected identities</p>
                {detail.identities.length === 0 ? (
                  <p style={{ fontSize: 13, color: "var(--text-muted)", margin: 0 }}>None</p>
                ) : (
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {detail.identities.map((i) => (
                      <Badge key={i.provider} variant="info">{i.provider}</Badge>
                    ))}
                  </div>
                )}
              </div>

              {/* ── Redeemed key ─────────────────────────────────────── */}
              {detail.redeemedKey && (
                <div>
                  <p className="eyebrow" style={{ fontSize: 10, marginBottom: 6 }}>Redeemed plan key</p>
                  <div style={{ fontSize: 13, color: "var(--text-primary)" }}>
                    <span style={{ fontFamily: "var(--font-mono)" }}>{detail.redeemedKey.code}</span>
                    <span style={{ color: "var(--text-muted)", marginLeft: 8 }}>
                      {detail.redeemedKey.durationDays != null ? `${detail.redeemedKey.durationDays}d` : "lifetime"} ·{" "}
                      {formatDate(detail.redeemedKey.redeemedAt)}
                    </span>
                  </div>
                </div>
              )}

              {/* ── Exploration map ──────────────────────────────────── */}
              <div>
                <p className="eyebrow" style={{ fontSize: 10, marginBottom: 6 }}>Exploration map</p>
                <OpenShowcaseButton userId={userId} />
              </div>

              {/* ── Recent activities ────────────────────────────────── */}
              <div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                  <p className="eyebrow" style={{ fontSize: 10, margin: 0 }}>Recent activities</p>
                  {user.activityCount > 0 && (
                    <Link
                      to="/admin/users/$userId/activities"
                      params={{ userId }}
                      style={{ display: "inline-flex", alignItems: "center", gap: 2, fontSize: 12, color: "var(--sky-300)", textDecoration: "none" }}
                    >
                      View all {user.activityCount.toLocaleString()}
                      <ChevronRight size={13} />
                    </Link>
                  )}
                </div>
                {detail.recentActivities.length === 0 ? (
                  <p style={{ fontSize: 13, color: "var(--text-muted)", margin: 0 }}>No activities</p>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    {detail.recentActivities.map((a) => (
                      <Link
                        key={a.id}
                        to="/admin/users/$userId/activities/$activityId"
                        params={{ userId, activityId: a.id }}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 10,
                          padding: "7px 0",
                          borderBottom: "1px solid var(--border-subtle)",
                          fontSize: 12,
                          textDecoration: "none",
                        }}
                      >
                        <span style={{ color: "var(--text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {a.name}
                        </span>
                        <span style={{ color: "var(--text-muted)", flexShrink: 0 }} className="tnum">
                          {a.sourceType} · {formatDate(a.startedAt)}
                        </span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* ── Danger zone ──────────────────────────────────────── */}
              {!isSelf && (
                <div
                  style={{
                    marginTop: 4,
                    padding: "12px 14px",
                    border: "1px solid rgba(224, 80, 63, 0.35)",
                    borderRadius: "var(--radius-md)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                  }}
                >
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)" }}>Delete account</div>
                    <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
                      Anonymise and sign out this user
                    </div>
                  </div>
                  <Button variant="danger" size="sm" onClick={() => setShowDelete(true)}>
                    Delete
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {showDelete && user && (
        <DeleteUserDialog
          email={displayEmail(user.email)}
          onCancel={() => setShowDelete(false)}
          onConfirm={() => deleteMutation.mutate()}
          pending={deleteMutation.isPending}
          error={
            deleteMutation.isError
              ? errorMessage(deleteMutation.error, "Failed to delete user")
              : null
          }
        />
      )}
    </div>
  );
}

// ─── AdminUsersPage ───────────────────────────────────────────────────────────

const PLAN_FILTERS = [
  { label: "All", value: "" },
  { label: "Free", value: "free" },
  { label: "Pro", value: "pro" },
  { label: "Lifetime", value: "lifetime" },
];

// Strava-only signups carry `strava-{id}@athlete.atlas.internal` until they
// add an address; anonymised accounts carry `deleted+{id}@atlas.internal`.
// Neither is a real inbox, so never present them as one.
function displayEmail(email: string): string {
  return isPlaceholderEmail(email) ? "No email yet" : email;
}

export default function AdminUsersPage() {
  const { user } = useAuth();
  const initialSearch = useSearch({ strict: false }) as { q?: string };
  const [search, setSearch] = React.useState(initialSearch.q ?? "");
  const [plan, setPlan] = React.useState("");
  const [showDeleted, setShowDeleted] = React.useState(false);
  const [offset, setOffset] = React.useState(0);
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [sort, setSort] = React.useState<NonNullable<AdminUserListParams["sort"]>>("created_at");

  const debouncedSearch = useDebounced(search, 300);

  React.useEffect(() => {
    setOffset(0);
  }, [debouncedSearch, plan, showDeleted, sort]);

  const usersQuery = useQuery({
    queryKey: ["admin/users", debouncedSearch, plan, showDeleted, sort, offset],
    queryFn: () =>
      fetchAdminUsers({
        search: debouncedSearch,
        plan,
        includeDeleted: showDeleted,
        sort,
        order: "desc",
        limit: PAGE_SIZE,
        offset,
      }),
    enabled: !!user?.isAdmin,
  });

  if (!user?.isAdmin) return <AccessDenied />;

  const rows = usersQuery.data?.users ?? [];
  const total = usersQuery.data?.total ?? 0;

  const gridColumns = "minmax(0,2fr) minmax(0,1.4fr) 76px 64px 70px 80px 96px 86px";
  const headerCell: React.CSSProperties = {
    fontSize: 11,
    fontWeight: 600,
    color: "var(--text-muted)",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  };

  return (
    <AdminShell title="Users">
      {/* ── Search + filter chips ─────────────────────────────────────── */}
      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <div style={{ position: "relative", width: 300 }}>
          <Search size={14} style={{ position: "absolute", left: 10, top: 10, color: "var(--text-muted)" }} />
          <input
            type="text"
            placeholder="Search by email or name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ ...inputStyle, paddingLeft: 32 }}
          />
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {PLAN_FILTERS.map((f) => {
            const active = plan === f.value;
            return (
              <button
                key={f.value}
                onClick={() => setPlan(f.value)}
                style={{
                  height: 28,
                  padding: "0 12px",
                  borderRadius: "var(--radius-pill)",
                  border: `1px solid ${active ? "var(--border-gold)" : "var(--border-default)"}`,
                  background: active ? "var(--accent-soft)" : "transparent",
                  color: active ? "var(--text-accent)" : "var(--text-secondary)",
                  fontSize: 12,
                  fontWeight: active ? 600 : 500,
                  cursor: "pointer",
                }}
              >
                {f.label}
              </button>
            );
          })}
          <button
            onClick={() => setShowDeleted((v) => !v)}
            title="Soft-deleted accounts are anonymised and hidden by default"
            style={{
              height: 28,
              padding: "0 12px",
              marginLeft: 6,
              borderRadius: "var(--radius-pill)",
              border: `1px dashed ${showDeleted ? "var(--border-gold)" : "var(--border-default)"}`,
              background: showDeleted ? "var(--accent-soft)" : "transparent",
              color: showDeleted ? "var(--text-accent)" : "var(--text-muted)",
              fontSize: 12,
              fontWeight: showDeleted ? 600 : 500,
              cursor: "pointer",
            }}
          >
            {showDeleted ? "Showing deleted" : "Show deleted"}
          </button>
        </div>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10 }}>
          <Segmented
            value={sort}
            options={[
              { value: "created_at", label: "Joined" },
              { value: "last_seen", label: "Last seen" },
              { value: "last_sync", label: "Last sync" },
            ]}
            onChange={setSort}
          />
          <span style={{ fontSize: 12, color: "var(--text-muted)" }} className="tnum">
            {total.toLocaleString()} explorer{total === 1 ? "" : "s"}
          </span>
        </div>
      </div>

      {/* ── Table ─────────────────────────────────────────────────────── */}
      <div
        style={{
          background: "var(--surface-card)",
          border: "1px solid var(--border-subtle)",
          borderRadius: "var(--radius-lg)",
          boxShadow: "var(--shadow-sm), var(--ring-top)",
          padding: "6px 18px 12px",
          overflowX: "auto",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: gridColumns,
            gap: 12,
            padding: "10px 0 8px",
            borderBottom: "1px solid var(--border-subtle)",
            minWidth: 840,
          }}
        >
          <span style={headerCell}>Email</span>
          <span style={headerCell}>Name</span>
          <span style={headerCell}>Plan</span>
          <span style={{ ...headerCell, textAlign: "right" }}>Score</span>
          <span style={{ ...headerCell, textAlign: "right" }}>Activities</span>
          <span style={{ ...headerCell, textAlign: "right" }}>Joined</span>
          <span style={{ ...headerCell, textAlign: "right" }}>Last seen</span>
          <span style={{ ...headerCell, textAlign: "right" }}>Last sync</span>
        </div>

        {usersQuery.isLoading ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: "12px 0" }}>
            <Skel height={30} />
            <Skel height={30} />
            <Skel height={30} />
            <Skel height={30} />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<Users size={20} />}
            title="No explorers found"
            hint={debouncedSearch ? "Try a different search term or clear the plan filter." : "No users match this filter yet."}
            style={{ margin: "12px 0" }}
          />
        ) : (
          rows.map((row: AdminUserSummary) => (
            <div
              key={row.id}
              onClick={() => setSelectedId(row.id)}
              style={{
                display: "grid",
                gridTemplateColumns: gridColumns,
                gap: 12,
                alignItems: "center",
                padding: "9px 0",
                borderBottom: "1px solid var(--border-subtle)",
                cursor: "pointer",
                minWidth: 840,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "var(--surface-hover)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
            >
              <span style={{ fontSize: 13, color: "var(--text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {isPlaceholderEmail(row.email) ? (
                  <span style={{ color: "var(--text-muted)", fontStyle: "italic" }}>No email yet</span>
                ) : (
                  row.email
                )}
                {row.isAdmin && <ShieldAlert size={12} style={{ marginLeft: 6, color: "var(--coral-300)", verticalAlign: "-2px" }} />}
              </span>
              <span style={{ fontSize: 13, color: "var(--text-secondary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {row.name ?? "—"}
              </span>
              <span><PlanBadge plan={row.plan} /></span>
              <span className="tnum" style={{ fontSize: 12, color: "var(--text-primary)", fontFamily: "var(--font-mono)", textAlign: "right" }}>
                {row.outdoorScore.toLocaleString()}
              </span>
              <span className="tnum" style={{ fontSize: 12, color: "var(--text-primary)", fontFamily: "var(--font-mono)", textAlign: "right" }}>
                {row.activityCount.toLocaleString()}
              </span>
              <span className="tnum" style={{ fontSize: 12, color: "var(--text-muted)", textAlign: "right" }}>
                {formatDate(row.createdAt)}
              </span>
              <span
                className="tnum"
                style={{ fontSize: 12, color: row.lastSeenAt ? "var(--text-primary)" : "var(--text-faint)", textAlign: "right", display: "inline-flex", justifyContent: "flex-end", alignItems: "center", gap: 5 }}
                title={row.lastSeenAt ?? "Not seen since presence tracking began"}
              >
                {row.lastSeenAt && <ClientGlyph client={row.lastSeenClient} size={12} />}
                {formatRelative(row.lastSeenAt)}
              </span>
              <span className="tnum" style={{ fontSize: 12, color: "var(--text-muted)", textAlign: "right" }} title={row.lastSyncAt ?? undefined}>
                {row.lastSyncAt ? formatRelative(row.lastSyncAt) : "—"}
              </span>
            </div>
          ))
        )}

        {/* ── Pagination ──────────────────────────────────────────────── */}
        {total > PAGE_SIZE && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 10, paddingTop: 12 }}>
            <span style={{ fontSize: 12, color: "var(--text-muted)" }} className="tnum">
              {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total.toLocaleString()}
            </span>
            <Button variant="secondary" size="sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>
              Previous
            </Button>
            <Button variant="secondary" size="sm" disabled={offset + PAGE_SIZE >= total} onClick={() => setOffset(offset + PAGE_SIZE)}>
              Next
            </Button>
          </div>
        )}
      </div>

      {selectedId && <UserDrawer userId={selectedId} onClose={() => setSelectedId(null)} />}
    </AdminShell>
  );
}
